<div class="modal-header border-0 pb-0 align-items-center">
  <h5 class="modal-title mb-0">{{ t()["share.tabShare"] }}</h5>
  <button
    type="button"
    class="btn-close ms-auto"
    aria-label="Close"
    (click)="activeModal.dismiss()"
  ></button>
</div>

<div class="modal-body pt-2">
  @if (loading()) {
    <div class="alert alert-info py-2 mb-0">
      {{ t()["share.loadingSettings"] }}
    </div>
  } @else {
    <p class="text-body-secondary mb-2">
      {{ t()["share.publishDescription"] }}
    </p>

    <form class="d-flex flex-column gap-2" [formGroup]="form" novalidate>
      <div class="row g-2">
        <div class="col-12 col-sm-6">
          <label class="form-label mb-1" for="share-category">{{
            t()["share.chooseCategory"]
          }}</label>
          <select
            id="share-category"
            #categorySelect
            class="form-select"
            formControlName="categoryId"
            required
            [class.is-invalid]="
              form.controls.categoryId.invalid &&
              form.controls.categoryId.touched
            "
            [attr.aria-label]="t()['share.chooseCategory']"
          >
            <option [ngValue]="null">
              {{ t()["share.chooseCategory"] }}
            </option>
            @for (category of categories(); track category.id) {
              <option [ngValue]="category.id">{{ category.name }}</option>
            }
          </select>
          @if (
            form.controls.categoryId.invalid && form.controls.categoryId.touched
          ) {
            <div class="invalid-feedback">
              {{ t()["share.errorCategoryRequired"] }}
            </div>
          }
        </div>
        <div class="col-12 col-sm-6">
          <label class="form-label mb-1" for="share-language">{{
            t()["share.chooseLanguage"]
          }}</label>
          <select
            id="share-language"
            class="form-select"
            formControlName="languageCode"
            required
            [class.is-invalid]="
              form.controls.languageCode.invalid &&
              form.controls.languageCode.touched
            "
          >
            <option [ngValue]="null">{{ t()["share.chooseLanguage"] }}</option>
            @for (language of languages(); track language.code) {
              <option [ngValue]="language.code">{{ language.name }}</option>
            }
          </select>
          @if (
            form.controls.languageCode.invalid &&
            form.controls.languageCode.touched
          ) {
            <div class="invalid-feedback">
              {{ t()["share.errorLanguageRequired"] }}
            </div>
          }
        </div>
      </div>

      <div class="row g-2">
        <div class="col-12">
          <label class="form-label mb-1" for="share-description">{{
            t()["share.description"]
          }}</label>
          <textarea
            id="share-description"
            class="form-control"
            rows="2"
            maxlength="300"
            formControlName="description"
            [placeholder]="t()['share.descriptionPlaceholder']"
            [attr.aria-label]="t()['share.description']"
          ></textarea>
        </div>
      </div>

      <div class="d-flex flex-wrap gap-2 column-gap-4">
        <div class="form-check">
          <input
            id="allow-copy"
            type="checkbox"
            class="form-check-input"
            formControlName="allowCopy"
          />
          <label class="form-check-label" for="allow-copy">{{
            t()["share.allowCopy"]
          }}</label>
        </div>

        <div class="form-check">
          <input
            id="allow-export"
            type="checkbox"
            class="form-check-input"
            formControlName="allowExport"
          />
          <label class="form-check-label" for="allow-export">{{
            t()["share.allowExport"]
          }}</label>
        </div>
      </div>

      <div class="share-link-block">
        <div
          class="nav nav-pills gap-1 mb-2"
          role="group"
          [attr.aria-label]="t()['share.tabShare']"
        >
          <button
            type="button"
            class="nav-link py-1 px-2"
            [class.active]="copyTab() === 'link'"
            [attr.aria-pressed]="copyTab() === 'link'"
            (click)="selectCopyTab('link')"
          >
            {{ t()["share.publicLink"] }}
          </button>
          <button
            type="button"
            class="nav-link py-1 px-2"
            [class.active]="copyTab() === 'embed'"
            [attr.aria-pressed]="copyTab() === 'embed'"
            (click)="selectCopyTab('embed')"
          >
            {{ t()["share.embed"] }}
          </button>
        </div>
        @if (copyTab() === "link") {
          <div class="position-relative">
            <input
              type="text"
              class="form-control pe-7"
              [attr.aria-label]="t()['share.publicLink']"
              [value]="publicLink()"
              readonly
            />
            <button
              type="button"
              class="btn btn-light btn-icon waves-effect share-copy-btn"
              [disabled]="!canSharePublicLink()"
              [attr.aria-label]="t()['share.copyLinkAria']"
              (click)="copyPublicLink()"
            >
              <svg class="sa-icon sa-bold sa-nofill" aria-hidden="true">
                <use href="/assets/icons/sprite.svg#copy"></use>
              </svg>
            </button>
          </div>
          <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
            <button
              type="button"
              class="btn btn-light btn-icon waves-effect social-btn"
              [disabled]="!canSharePublicLink()"
              [attr.aria-label]="t()['share.linkedInAria']"
              (click)="shareOnLinkedIn()"
            >
              <img src="/assets/icons/linkedin-color.svg" alt="" />
            </button>
            <button
              type="button"
              class="btn btn-light btn-icon waves-effect social-btn"
              [disabled]="!canSharePublicLink()"
              [attr.aria-label]="t()['share.facebookAria']"
              (click)="shareOnFacebook()"
            >
              <img src="/assets/icons/facebook-color.svg" alt="" />
            </button>
            <button
              type="button"
              class="btn btn-light btn-icon waves-effect social-btn"
              [disabled]="!canSharePublicLink()"
              [attr.aria-label]="t()['share.instagramAria']"
              (click)="shareOnInstagram()"
            >
              <img src="/assets/icons/instagram-color.svg" alt="" />
            </button>
          </div>
        } @else {
          <label class="visually-hidden" for="share-embed">{{
            t()["share.embedCode"]
          }}</label>
          <textarea
            id="share-embed"
            class="form-control font-monospace small"
            rows="3"
            readonly
            [value]="embedCode()"
          ></textarea>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm mt-2"
            [disabled]="!embedCode()"
            (click)="copyEmbedCode()"
          >
            {{ t()["share.copyEmbed"] }}
          </button>
        }
        @if (!isPublished()) {
          <small class="text-body-secondary d-block mt-1">{{
            t()["share.publishFirst"]
          }}</small>
        }
        @if (copied()) {
          <small role="status" class="text-success mt-1 d-inline-block">{{
            t()["share.copied"]
          }}</small>
        }
      </div>
    </form>

    @if (errorMessage()) {
      <div class="alert alert-danger mt-3 mb-0">{{ errorMessage() }}</div>
    }
  }
</div>

<div class="modal-footer border-0 pt-2">
  <button
    type="button"
    class="btn btn-outline-primary"
    (click)="activeModal.dismiss()"
  >
    {{ t()["share.cancel"] }}
  </button>

  @if (isPublished()) {
    <button
      type="button"
      class="btn btn-outline-danger"
      [disabled]="loading() || saving()"
      (click)="unpublish()"
    >
      {{ t()["share.unpublishMap"] }}
    </button>
  }
  <button
    type="button"
    class="btn btn-primary"
    [disabled]="loading() || saving() || form.invalid"
    (click)="publish()"
  >
    {{
      saving()
        ? t()["share.publishing"]
        : isPublished()
          ? t()["share.updateMap"]
          : t()["share.publishMap"]
    }}
  </button>
</div>
